import React, { useState, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import { Link } from 'gatsby'
import { useLocation } from '@reach/router'

import Button from '../common/Button'
import Container from '../common/Container'
import MenuContainer from './HeaderMenuContainer/HeaderMenuContainer'
import HeaderNavbarMobile from '../HeaderNavbarMobile/HeaderNavbarMobile'
import HeaderBar, { HeaderBarTheme } from '../HeaderBar/HeaderBar'

import { ReactComponent as LogoSvg } from './fpjs.svg'
import { ReactComponent as LogoDarkSvg } from './fpjsDark.svg'
import { ReactComponent as StarSVG } from './StarSVG.svg'

import { URL, PATH } from '../../constants/content'
import { kFormatter } from '../../helpers/format'
import { useAmpli } from '../../helpers/amplitude'
import { scrollToElementById } from '../../helpers/scrollToElementByID'
import { useGithubFpjs } from '../../context/GithubContext'
import { useSystemStatus } from '../../context/SystemStatusContext'
import useSessionStorage from '../../hooks/useSessionStorage'

import styles from './Header.module.scss'

interface HeaderProps {
  notificationBar?: {
    arrowText?: string
    barBody?: string
    url?: string
    showNotificationBell?: boolean
    showEmoji?: boolean
    emoji?: string
    theme?: HeaderBarTheme
  }
  darkMode?: boolean
  isHomepage?: boolean
  demoMenuEmbeddedExperiment?: boolean
}

export enum DropdownId {
  Platform = 'platform',
  Solutions = 'solutions',
  Developers = 'developers',
  Resources = 'resources',
  Pricing = 'pricing',
  Demo = 'demo',
}

export default function Header({ notificationBar, darkMode, isHomepage, demoMenuEmbeddedExperiment }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [showNotificationBar, toggleNotificationBar] = useSessionStorage<boolean>('showNotificationBar', true)
  const { pathname } = useLocation()
  const { githubData } = useGithubFpjs()
  const { systemStatus } = useSystemStatus()
  const { ampli, botDetected } = useAmpli()
  const [shadow, setShadow] = useState(false)

  const handleScroll = () => {
    if (window.scrollY > 0) {
      setShadow(true)
    } else {
      setShadow(false)
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    const mobileBodyClass = 'isMobileMenuOpen'
    if (isMobileMenuOpen) {
      document.body.classList.add(mobileBodyClass)
    } else {
      document.body.classList.remove(mobileBodyClass)
    }
  }, [isMobileMenuOpen])

  const handleToggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const [selectedNavOption, setSelectedNavOption] = useState<DropdownId | null>(null)
  const [selectedNavOptionPosition, setSelectedNavOptionPosition] = useState<{ x: number }>({ x: 0 })

  const platform = useRef<HTMLButtonElement>(null)
  const solutions = useRef<HTMLButtonElement>(null)
  const developers = useRef<HTMLButtonElement>(null)
  const resources = useRef<HTMLButtonElement>(null)
  const demo = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const resizeHandler = () => {
      if (selectedNavOption !== null || selectedNavOptionPosition.x !== 0) {
        setSelectedNavOptionPosition({ x: 0 })
        setSelectedNavOption(null)
      }
    }
    document.addEventListener('resize', resizeHandler)

    return () => document.removeEventListener('resize', resizeHandler)
  }, [selectedNavOption, selectedNavOptionPosition.x])

  const onNavOptionHover = (navItemId: DropdownId) => {
    if (selectedNavOption === navItemId) return

    let navOptionScreenPosition: ClientRect

    if (navItemId === DropdownId.Pricing) {
      setSelectedNavOption(navItemId)
      setSelectedNavOptionPosition({ x: 0 })
      return
    }

    switch (navItemId) {
      case DropdownId.Platform:
        if (platform === null || platform.current === null) return
        navOptionScreenPosition = platform.current.getBoundingClientRect()
        break
      case DropdownId.Solutions:
        if (solutions === null || solutions.current === null) return
        navOptionScreenPosition = solutions.current.getBoundingClientRect()
        break
      case DropdownId.Developers:
        if (developers === null || developers.current === null) return
        navOptionScreenPosition = developers.current.getBoundingClientRect()
        break
      case DropdownId.Resources:
        if (resources === null || resources.current === null) return
        navOptionScreenPosition = resources.current.getBoundingClientRect()
        break
      case DropdownId.Demo:
        if (demo === null || demo.current === null) return
        navOptionScreenPosition = demo.current.getBoundingClientRect()
        break
      default:
        return
    }

    const viewportWidth = window.innerWidth
    const adjustedXPosition = (navOptionScreenPosition.left / viewportWidth) * 100

    setSelectedNavOptionPosition({ x: adjustedXPosition })
    setSelectedNavOption(navItemId)
  }

  const onMouseLeave = () => {
    setSelectedNavOption(null)
    setSelectedNavOptionPosition({ x: 0 })
  }

  return (
    <>
      <Container
        size='large'
        className={classNames(styles.root, {
          [styles.darkRoot]: darkMode,
          [styles.isHomepage]: isHomepage,
          [styles.shadow]: shadow,
        })}
      >
        <header className={styles.header} onMouseLeave={onMouseLeave}>
          <nav className={styles.navLeft}>
            <Link to='/' title='Logo'>
              {darkMode ? <LogoDarkSvg className={styles.logo} /> : <LogoSvg className={styles.logo} />}
            </Link>
          </nav>
          <nav className={styles.navCenter}>
            <ul className={classNames(styles.links, styles.desktopOnly)}>
              <HeaderItem
                dropdownId={DropdownId.Platform}
                activeDropdown={selectedNavOption}
                handleMouseEnter={onNavOptionHover}
                darkMode={darkMode}
              >
                <button
                  ref={platform}
                  className={classNames(styles.link, {
                    [styles.linkIsOpen]: selectedNavOption === DropdownId.Platform,
                  })}
                >
                  Product
                </button>
              </HeaderItem>
              <HeaderItem
                dropdownId={DropdownId.Solutions}
                activeDropdown={selectedNavOption}
                handleMouseEnter={onNavOptionHover}
                darkMode={darkMode}
              >
                <button
                  ref={solutions}
                  className={classNames(styles.link, {
                    [styles.linkIsOpen]: selectedNavOption === DropdownId.Solutions,
                  })}
                >
                  Use Cases
                </button>
              </HeaderItem>
              <HeaderItem
                dropdownId={DropdownId.Developers}
                activeDropdown={selectedNavOption}
                handleMouseEnter={onNavOptionHover}
                darkMode={darkMode}
              >
                <button
                  ref={developers}
                  className={classNames(styles.link, {
                    [styles.linkIsOpen]: selectedNavOption === DropdownId.Developers,
                  })}
                >
                  Developers
                </button>
              </HeaderItem>
              <HeaderItem
                dropdownId={DropdownId.Resources}
                activeDropdown={selectedNavOption}
                handleMouseEnter={onNavOptionHover}
                darkMode={darkMode}
              >
                <button
                  ref={resources}
                  className={classNames(styles.link, {
                    [styles.linkIsOpen]: selectedNavOption === DropdownId.Resources,
                  })}
                >
                  Resources
                </button>
              </HeaderItem>

              {!demoMenuEmbeddedExperiment && (
                <HeaderItem
                  dropdownId={DropdownId.Demo}
                  activeDropdown={selectedNavOption}
                  handleMouseEnter={onNavOptionHover}
                  darkMode={darkMode}
                >
                  <button
                    ref={demo}
                    className={classNames(styles.link, {
                      [styles.linkIsOpen]: selectedNavOption === DropdownId.Demo,
                    })}
                  >
                    Demo
                  </button>
                </HeaderItem>
              )}

              <HeaderItem
                dropdownId={DropdownId.Pricing}
                activeDropdown={selectedNavOption}
                handleMouseEnter={onNavOptionHover}
                handleMouseLeave={(event) => {
                  event.stopPropagation()
                  onMouseLeave()
                }}
                darkMode={darkMode}
              >
                <Link
                  className={classNames(styles.link, styles.desktopOnly, {
                    [styles.linkIsOpen]: selectedNavOption === DropdownId.Pricing,
                  })}
                  to={PATH.pricingUrl}
                >
                  Pricing
                </Link>
              </HeaderItem>
            </ul>
          </nav>
          <nav className={styles.navRight}>
            <Button
              size='small'
              variant='ghost'
              className={classNames(styles.desktopOnly, styles.loginLink)}
              rel='noreferrer'
              href={URL.dashboardLoginUrl}
              onClick={() => {
                ampli.webCtaClicked({ ctaName: 'Login', 'Web Page Path': pathname, botDetected })
              }}
            >
              Login
            </Button>
            {pathname === PATH.start ? (
              <Button
                onClick={() => {
                  ampli.webCtaClicked({ ctaName: 'Contact Sales', 'Web Page Path': pathname, botDetected })
                  scrollToElementById('contact-sales')
                }}
                size='small'
                variant={'primary'}
                outlined
                className={styles.button}
              >
                Contact Sales
              </Button>
            ) : (
              <Button
                href={PATH.contactSales}
                size='small'
                variant={darkMode ? 'dark' : 'primary'}
                outlined
                className={styles.button}
                buttonId='contact-sales-top-nav'
                onClick={() => {
                  ampli.webCtaClicked({ ctaName: 'Contact Sales', 'Web Page Path': pathname, botDetected })
                }}
              >
                Contact Sales
              </Button>
            )}

            {pathname === PATH.botD ? (
              <Button
                variant='primary'
                size='small'
                className={styles.signupButton}
                onClick={() => {
                  ampli.webCtaClicked({ ctaName: 'Get Started', 'Web Page Path': pathname, botDetected })
                  scrollToElementById('get-started-bot-detection')
                }}
              >
                Get Started
              </Button>
            ) : (
              <Button
                variant='primary'
                size='small'
                className={styles.signupButton}
                href={URL.signupUrl}
                buttonId='get-started-top-nav'
                onClick={() => {
                  ampli.webCtaClicked({ ctaName: 'Get Started', 'Web Page Path': pathname, botDetected })
                }}
              >
                Get Started
              </Button>
            )}

            <button
              aria-label='Mobile Menu'
              className={classNames(styles.mobileToggler, { [styles.isOpen]: isMobileMenuOpen })}
              onClick={handleToggleMobileMenu}
            >
              {/* hamburger button */}
              <span />
              <span />
              <span />
              <span />
            </button>
          </nav>
          <MenuContainer
            selectedNavOption={selectedNavOption}
            selectedNavOptionPosition={selectedNavOptionPosition}
            darkMode={darkMode}
            demoMenuEmbeddedExperiment={demoMenuEmbeddedExperiment}
            systemStatus={systemStatus}
            githubData={githubData}
          />
        </header>
      </Container>
      {isMobileMenuOpen && (
        <HeaderNavbarMobile darkMode={darkMode} demoMenuEmbeddedExperiment={demoMenuEmbeddedExperiment} />
      )}
      {notificationBar && showNotificationBar && (
        <HeaderBar
          linkUrl={notificationBar?.url}
          arrowText={notificationBar?.arrowText}
          theme={notificationBar?.theme}
          showNotificationBell={notificationBar?.showNotificationBell}
          showEmoji={notificationBar?.showEmoji}
          emoji={notificationBar?.emoji}
          onClose={() => toggleNotificationBar(false)}
        >
          {
            <div
              dangerouslySetInnerHTML={{
                __html: notificationBar?.barBody ?? '',
              }}
            />
          }
        </HeaderBar>
      )}
    </>
  )
}

interface HeaderItemProps {
  dropdownId: DropdownId
  activeDropdown: DropdownId | null
  handleMouseEnter: (dropdownId: DropdownId) => void
  handleMouseLeave?: (event: React.MouseEvent<HTMLLIElement>) => void
  children: React.ReactNode
  darkMode?: boolean
}

export function HeaderItem({
  dropdownId,
  activeDropdown,
  handleMouseEnter,
  handleMouseLeave,
  children,
  darkMode,
}: HeaderItemProps) {
  return (
    <li
      className={classNames(styles.li, {
        [styles.activeLink]: activeDropdown === dropdownId,
        [styles.activeMenu]: activeDropdown !== null,
      })}
      onMouseEnter={() => handleMouseEnter(dropdownId)}
      onMouseLeave={handleMouseLeave}
    >
      {activeDropdown === dropdownId && (
        <div className={styles.background}>
          <motion.div
            style={{
              width: '100%',
              height: '29px',
              position: 'absolute',
              left: '0px',
              right: 0,
              borderRadius: '8px',
              marginBottom: '2px',
              border: darkMode ? '1px solid #232321' : '1px solid rgba(228, 229, 225, 0.80)',
              background: darkMode ? '#202021' : '#FAFAF8',
              boxShadow: darkMode
                ? '0px 0.5px 0.5px 0px rgba(114, 114, 115, 0.40)'
                : '0px 0.5px 0.5px 0px rgba(119, 54, 28, 0.20)',
              zIndex: -1,
            }}
            layoutId='background'
            transition={{ duration: 0.2, ease: [0.165, 0.84, 0.44, 1] }}
          />
        </div>
      )}
      {children}
    </li>
  )
}
export const communityDropdown = (stars?: number) => [
  { title: 'Discord', url: URL.discordServerURL },
  {
    title: (
      <div className={styles.githubLabel}>
        GitHub
        <span className={styles.githubStars}>
          <StarSVG className={styles.star} />
          {stars ? `${kFormatter(stars, true)}+` : '...'}
        </span>
      </div>
    ),
    url: URL.githubRepoUrl,
  },
]
